import type { TaskSurfaceCard } from "@/services/api/agentic-v2/types";
import {
  buildOpenworkQueueBreakdownPhrase,
  pickOpenworkPriorityTask,
  type OpenworkNextMeetingBrief,
  type OpenworkStandupBrief,
} from "@/components/home/standup/openworkStandupBrief";
import type { OsAniContextSnapshot } from "@/components/ui/os-layout/buildOsAniContextSnapshot";
import { getReviewSurfaceLiveContext } from "@/services/inAppAgent/reviewSurfaceAgentBridge";
import {
  reviewQueueClientName,
  sortReviewQueueTasks,
} from "@/components/agentic-surface/widgets/home/reviewQueueModel";
import { normalizeOpenworkLetterReviewQueue, type OpenworkLetterReviewTask } from "@/components/review/reviewDemoHelpers";

/** Runtime snapshot sent with each in-app agent turn (server merges with profile). */
export type InAppAgentRuntimeContext = {
  company_id?: string;
  surface_id?: string;
  refreshed_at?: string;
  standup?: {
    greeting_line?: string;
    lead_line?: string;
    narrative?: string;
    priority_client?: string | null;
    priority_task_id?: string | null;
    priority_task_name?: string | null;
  };
  review_queue?: Array<{
    task_id: string;
    name: string;
    client_name?: string | null;
    scenario_ref?: string | null;
  }>;
  review_queue_count?: number;
  next_meeting?: {
    client_name: string;
    title: string;
    date_label: string;
    time: string;
    briefing_ready?: boolean;
  } | null;
  home_summary?: {
    review_queue_phrase?: string;
  };
  case_checking?: {
    focused_case_id?: string | null;
  };
  user_surface?: {
    focused_app?: string;
    focused_window?: {
      app_id: string;
      title: string;
      task_id?: string | null;
      case_id?: string | null;
      mission_id?: string | null;
      fa_client_id?: string | null;
    } | null;
    review?: {
      task_id?: string | null;
      artifact_id?: string | null;
      surface_tab?: string | null;
      open_comment_count?: number;
      ask_ani_open?: boolean;
    };
  };
};

type AgentReviewQueueItem = NonNullable<InAppAgentRuntimeContext["review_queue"]>[number];

function isAddendumQueueItem(item: { scenario_ref?: string | null; name?: string }): boolean {
  const ref = item.scenario_ref?.trim() ?? "";
  if (ref.includes("addendum")) return true;
  return (item.name ?? "").toLowerCase().includes("addendum");
}

function pickOpenworkPriorityFromAgentQueue(
  queue: AgentReviewQueueItem[],
): AgentReviewQueueItem | null {
  if (queue.length === 0) return null;
  return queue.find((item) => !isAddendumQueueItem(item)) ?? queue[0] ?? null;
}

function clientNameForAgentQueue(task: TaskSurfaceCard): string | null {
  const fromEntity = reviewQueueClientName(task);
  if (fromEntity) return fromEntity;
  const match = task.name?.match(/\bfor\s+(.+)$/i);
  return match?.[1]?.trim() ?? null;
}

type ReviewQueueStandupBrief = {
  greetingLine: string;
  leadLine: string;
  narrative: string;
};

export function queueTaskId(task: TaskSurfaceCard): string {
  const openworkTask = task as OpenworkLetterReviewTask;
  return openworkTask.task_id ?? task._id;
}

export function buildReviewQueueInAppAgentContext(params: {
  companyId?: string;
  surfaceId?: string;
  briefing: ReviewQueueStandupBrief;
  reviewTasks: TaskSurfaceCard[];
  nextMeeting?: OpenworkNextMeetingBrief | null;
  priorityTask?: TaskSurfaceCard | null;
}): InAppAgentRuntimeContext {
  const sortedPending = sortReviewQueueTasks(
    params.reviewTasks.filter((task) => task.status !== "queued"),
    null,
  );
  const pendingPriority = params.priorityTask ?? sortedPending[0] ?? null;

  return {
    company_id: params.companyId,
    surface_id: params.surfaceId,
    refreshed_at: new Date().toISOString(),
    standup: {
      greeting_line: params.briefing.greetingLine,
      lead_line: params.briefing.leadLine,
      narrative: params.briefing.narrative,
      priority_client: pendingPriority ? clientNameForAgentQueue(pendingPriority) : null,
      priority_task_id: pendingPriority ? queueTaskId(pendingPriority) : null,
      priority_task_name: pendingPriority?.name ?? null,
    },
    review_queue: sortedPending.map((task) => ({
      task_id: queueTaskId(task),
      name: task.name,
      client_name: clientNameForAgentQueue(task),
      scenario_ref: task.scenario_ref ?? null,
    })),
    review_queue_count: sortedPending.length,
    next_meeting: params.nextMeeting
      ? {
          client_name: params.nextMeeting.clientName,
          title: params.nextMeeting.title,
          date_label: params.nextMeeting.dateLabel,
          time: params.nextMeeting.time,
          briefing_ready: true,
        }
      : null,
  };
}

export function buildOpenworkInAppAgentContext(params: {
  companyId?: string;
  surfaceId?: string;
  briefing: OpenworkStandupBrief;
  reviewTasks: TaskSurfaceCard[];
  nextMeeting?: OpenworkNextMeetingBrief | null;
  assigneeEmail?: string | null;
}): InAppAgentRuntimeContext {
  const queueTasks = normalizeOpenworkLetterReviewQueue(params.reviewTasks, {
    assigneeEmail: params.assigneeEmail,
  });
  const sortedPending = sortReviewQueueTasks(
    queueTasks.filter((task) => task.status !== "queued"),
    null,
  );
  const pendingPriority = pickOpenworkPriorityTask(queueTasks);
  const queuePhrase = buildOpenworkQueueBreakdownPhrase(sortedPending);

  return {
    ...buildReviewQueueInAppAgentContext({
      companyId: params.companyId,
      surfaceId: params.surfaceId,
      briefing: params.briefing,
      reviewTasks: queueTasks,
      nextMeeting: params.nextMeeting,
      priorityTask: pendingPriority,
    }),
    home_summary: queuePhrase ? { review_queue_phrase: queuePhrase } : undefined,
  };
}

/** Patch cached home context with live OS state at message send time. */
export function refreshInAppAgentContextForSend(
  base: InAppAgentRuntimeContext | Record<string, unknown> | undefined,
  osContext: OsAniContextSnapshot,
): InAppAgentRuntimeContext | undefined {
  if (!base || typeof base !== "object") return undefined;

  const context = base as InAppAgentRuntimeContext;
  const queue = context.review_queue ?? [];
  const nextPriority = pickOpenworkPriorityFromAgentQueue(queue);
  const focused = osContext.focusedWindow;
  const focusedTaskId = focused?.params.taskId ?? focused?.params.task_id ?? null;
  const focusedCaseId = focused?.params.caseId ?? focused?.params.case_id ?? null;
  const focusedMissionId = focused?.params.missionId ?? focused?.params.mission_id ?? null;
  const focusedFaClientId =
    focused?.params.faClientId ?? focused?.params.fa_client_id ?? null;
  const liveReview = getReviewSurfaceLiveContext();
  const reviewFocused =
    focused?.appId === "task-review" ||
    focused?.appId === "artifact" ||
    Boolean(liveReview?.artifactId);
  const reviewSurface =
    liveReview && reviewFocused
      ? {
          task_id: liveReview.taskId ?? focusedTaskId,
          artifact_id: liveReview.artifactId ?? null,
          surface_tab: liveReview.surfaceTab ?? null,
          open_comment_count: liveReview.openCommentCount ?? 0,
          ask_ani_open: liveReview.askAniOpen ?? false,
        }
      : undefined;

  const standup =
    context.standup && nextPriority
      ? {
          ...context.standup,
          priority_client: nextPriority.client_name ?? null,
          priority_task_id: nextPriority.task_id ?? null,
          priority_task_name: nextPriority.name ?? null,
        }
      : context.standup;

  return {
    ...context,
    refreshed_at: new Date().toISOString(),
    standup,
    case_checking:
      focused?.appId === "case-checking" && focusedCaseId
        ? { focused_case_id: focusedCaseId }
        : undefined,
    user_surface: {
      focused_app: osContext.focusedApp,
      focused_window: focused
        ? {
            app_id: focused.appId,
            title: focused.title,
            task_id: focusedTaskId,
            case_id: focusedCaseId,
            mission_id: focusedMissionId,
            fa_client_id: focusedFaClientId,
          }
        : null,
      ...(reviewSurface ? { review: reviewSurface } : {}),
    },
  };
}
